import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const BLUE_BORDER = '#5A9ECB';
const BLUE_BG = '#EFF6FF';
const BLUE_TEXT = '#0c4a6e';

/**
 * Fixed-position speech bubble anchored to the condition <select> via
 * getBoundingClientRect. Tail points down at the dropdown; dismisses on
 * outside click (excluding the anchor select) or any scroll.
 */
function ConditionDescriptionPopover({ anchorRef, visible, description, onDismiss }) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [fadedIn, setFadedIn] = useState(false);

  const updatePosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) {
      setPosition(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setPosition({
      centerX: rect.left + rect.width / 2,
      top: rect.top - 10,
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!visible) {
      setPosition(null);
      setFadedIn(false);
      return undefined;
    }
    updatePosition();
    const raf = requestAnimationFrame(() => setFadedIn(true));
    window.addEventListener('resize', updatePosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  useEffect(() => {
    if (!visible) return undefined;

    const handleScroll = () => onDismiss();

    const handlePointerDown = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorRef?.current?.contains(event.target)) return;
      onDismiss();
    };

    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [visible, onDismiss, anchorRef]);

  if (!visible || !position || !description) return null;

  return createPortal(
    <div
      ref={popoverRef}
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: position.centerX,
        top: position.top,
        transform: 'translate(-50%, -100%)',
        zIndex: 10000,
        maxWidth: 360,
        width: 'max-content',
        minWidth: 260,
        pointerEvents: 'auto',
        fontFamily: 'Nunito, sans-serif',
        opacity: fadedIn ? 1 : 0,
        transition: 'opacity 250ms ease-out',
      }}
    >
      <div
        style={{
          backgroundColor: BLUE_BG,
          border: `1.5px solid ${BLUE_BORDER}`,
          borderRadius: 12,
          padding: '12px 14px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          fontSize: 14,
          fontWeight: 400,
          color: BLUE_TEXT,
          lineHeight: 1.45,
        }}
      >
        {description}
      </div>
      <div
        style={{
          position: 'relative',
          width: 16,
          height: 8,
          margin: '0 auto',
          marginTop: -1,
        }}
        aria-hidden="true"
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: `8px solid ${BLUE_BORDER}`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: `6px solid ${BLUE_BG}`,
          }}
        />
      </div>
    </div>,
    document.body
  );
}

export default ConditionDescriptionPopover;
